import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { FreelancerDetailsResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  work_email: string;
};

interface Props {
  freelancer: FreelancerDetailsResource;
  onClose: () => void;
}

const WorkEmailFormModal: FC<Props> = ({ freelancer, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: { work_email: freelancer.workEmail ?? '' },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.put(
          `/v1/payments/freelancers/work-email/${freelancer.id}`,
          data,
        );
        await queryClient.invalidateQueries(['freelancers_details'], {
          exact: false,
        });
        alerts.success('Work email updated successfully');
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel heading="Edit Work Email" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="work_email"
                label="Work Email"
                placeholder="Add email"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Update
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default WorkEmailFormModal;
